import {Button, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment, useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {MyProfileEnum} from 'main/analytics/gaTagsEnum';
import {DiscardChangesModal} from 'presentation/components/ChangeData/DiscardChangesModal';
import {useContractStatus, usePersonalData} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {FlowEnum, ufNameMap} from 'presentation/pages/private/booking/constants';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText, getUfPath} from 'presentation/utils';
import {useChangeDataFormStateStore} from '../../store/changeDataFormStateStore';
import {LocationFieldProps} from './types';

const {selectLocale, changeLocale} = translations['pt-br'].commons;
const {locationSelection} = translations['pt-br'].privateFlow.profile;

export function LocationField({flowType}: LocationFieldProps) {
	const navigate = useNavigate();
	const {palette} = useTheme();

	const {isFormDirty} = useChangeDataFormStateStore(state => state.formState);

	const [isDiscardChangesModalOpenState, setIsDiscardChangesModalOpenState] = useState(false);

	const {userLocation, emptyLocation} = usePersonalData();
	const {currentLocation} = useAccountInformation();

	const isAuthorizationFlow = flowType === FlowEnum.AUTHORIZATIONS;

	const authorizationLocationEmpty = !currentLocation.city || !currentLocation.state.cdUf;
	const profileAndBookingLocationEmpty = !currentLocation.city && emptyLocation;

	const isEmpty = isAuthorizationFlow
		? authorizationLocationEmpty
		: profileAndBookingLocationEmpty;

	const bookingUfPath = getUfPath(flowType);

	const cityName = isAuthorizationFlow
		? currentLocation.city
		: currentLocation.city || userLocation.nmCidade;

	const ufName = isAuthorizationFlow
		? currentLocation.state.nmUf
		: currentLocation.state.nmUf || ufNameMap[userLocation.nmUf];

	const handleNavToStateSelection = useCallback(() => {
		if (isFormDirty) {
			setIsDiscardChangesModalOpenState(true);
			return;
		}
		navigate(bookingUfPath);
	}, [bookingUfPath, isFormDirty]);

	const {isValid, isCancelledAtFiveYears} = useContractStatus();

	if (isEmpty) {
		return (
			<Fragment>
				<Stack>
					<Typography variant="body1" fontSize={'0.875rem'} color="text.secondary">
						{locationSelection.locationEmpty}
					</Typography>
					<Button
						variant="text"
						onClick={handleNavToStateSelection}
						sx={{alignSelf: 'flex-end'}}>
						{selectLocale}
					</Button>
				</Stack>
				<DiscardChangesModal
					open={isDiscardChangesModalOpenState}
					setOpen={setIsDiscardChangesModalOpenState}
					onConfirm={() => navigate(bookingUfPath)}
				/>
			</Fragment>
		);
	}

	return (
		<Fragment>
			<Stack>
				<Stack direction="row" gap={1}>
					<Icon name="my-location-filled" size={20} color={palette.primary.main} />
					<Stack>
						<Typography variant="body1" color="text.primary">
							{formatText(cityName)}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{formatText(ufName)}
						</Typography>
					</Stack>
				</Stack>
				<Stack direction="row" justifyContent="flex-end">
					{isValid || isCancelledAtFiveYears ? (
						<Button
							variant="text"
							onClick={handleNavToStateSelection}
							data-tag-id={MyProfileEnum.CLICK_ALTERAR_LOCAL_DE_ATENDIMENTO}>
							{changeLocale}
						</Button>
					) : null}
				</Stack>
			</Stack>

			<DiscardChangesModal
				open={isDiscardChangesModalOpenState}
				setOpen={setIsDiscardChangesModalOpenState}
				onConfirm={() => navigate(bookingUfPath)}
			/>
		</Fragment>
	);
}
